import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { emailNotificationsApi } from '../../services/dispatchApi';
import { LoadingState, EmptyState } from '../Common/PanelState';

const FLARE_RED = '#c4151c';
const Icon = ({ path, className = 'h-5 w-5' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
  </svg>
);

const CATEGORY_ORDER = ['Account', 'Orders', 'Support', 'System'];

export default function EmailCenterSection() {
  const { user } = useAuth() || {};
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [items, setItems] = useState([]);
  const [enabled, setEnabled] = useState({});
  const [previewKey, setPreviewKey] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [testTo, setTestTo] = useState('');

  useEffect(() => {
    if (user?.email && !testTo) setTestTo(user.email);
  }, [user?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await emailNotificationsApi.list();
      const list = Array.isArray(data?.notifications) ? data.notifications : [];
      setItems(list);
      setEnabled(data?.enabled || Object.fromEntries(list.map((n) => [n.key, !!n.enabled])));
      setDirty(false);
      if (!previewKey && list[0]?.key) setPreviewKey(list[0].key);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not load email settings.');
    } finally {
      setLoading(false);
    }
  }, [previewKey]);

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const grouped = useMemo(() => {
    const map = {};
    items.forEach((item) => {
      const cat = item.category || 'Other';
      if (!map[cat]) map[cat] = [];
      map[cat].push(item);
    });
    return CATEGORY_ORDER.filter((c) => map[c]?.length).map((c) => ({ category: c, rows: map[c] }));
  }, [items]);

  const preview = items.find((i) => i.key === previewKey) || items[0] || null;

  const toggle = (key, canDisable) => {
    if (!canDisable) {
      toast.info('This email cannot be disabled.');
      return;
    }
    setEnabled((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await emailNotificationsApi.update(enabled);
      setEnabled(data?.enabled || enabled);
      setDirty(false);
      toast.success('Email notification settings saved.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!preview?.key) {
      toast.info('Select an email type first.');
      return;
    }
    const to = String(testTo || '').trim();
    if (!to || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      toast.error('Enter a valid email address.');
      return;
    }
    setTesting(true);
    try {
      const { data } = await emailNotificationsApi.sendTest(preview.key, to);
      toast.success(data?.message || `Test sent to ${to}`);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Test email failed.');
    } finally {
      setTesting(false);
    }
  };

  const enabledCount = Object.values(enabled).filter(Boolean).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <p className="text-[11px] font-black uppercase tracking-[0.2em] mb-1" style={{ color: FLARE_RED }}>
            Communications
          </p>
          <h1 className="admin-page-title">Email Center</h1>
          <p className="admin-page-sub max-w-2xl">
            Control every customer-facing email Flare sends — enable or disable each type, review what it contains,
            and preview the live HTML template. Delivery history is under Send History.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/admin/email-logs"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-bold text-gray-800 hover:bg-gray-50"
          >
            <Icon path="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            Send History
          </Link>
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-bold shadow-sm disabled:opacity-50"
            style={{ backgroundColor: FLARE_RED }}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <div className="text-xs font-semibold uppercase text-gray-500 tracking-wide">Email types</div>
          <div className="text-2xl font-extrabold text-gray-900 mt-1">{items.length}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <div className="text-xs font-semibold uppercase text-gray-500 tracking-wide">Currently enabled</div>
          <div className="text-2xl font-extrabold text-emerald-700 mt-1">{enabledCount}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <div className="text-xs font-semibold uppercase text-gray-500 tracking-wide">Unsaved changes</div>
          <div className={`text-2xl font-extrabold mt-1 ${dirty ? 'text-amber-700' : 'text-gray-400'}`}>
            {dirty ? 'Yes' : 'None'}
          </div>
        </div>
      </div>

      {loading ? (
        <LoadingState label="Loading email catalog…" />
      ) : items.length === 0 ? (
        <EmptyState title="No email types found" hint="Backend catalog is empty." />
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-6 items-start">
          <div className="xl:col-span-3 space-y-5">
            {grouped.map(({ category, rows }) => (
              <section key={category} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50/80 flex items-center justify-between">
                  <h2 className="text-sm font-black uppercase tracking-[0.14em] text-gray-600">{category}</h2>
                  <span className="text-xs font-semibold text-gray-400">{rows.length} type{rows.length === 1 ? '' : 's'}</span>
                </div>
                <div className="divide-y divide-gray-100">
                  {rows.map((row) => {
                    const on = !!enabled[row.key];
                    const selected = previewKey === row.key;
                    return (
                      <div
                        key={row.key}
                        className={`p-5 transition-colors ${selected ? 'bg-rose-50/40' : 'hover:bg-gray-50/70'}`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                          <button
                            type="button"
                            onClick={() => setPreviewKey(row.key)}
                            className="flex-1 text-left min-w-0"
                          >
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <h3 className="font-extrabold text-gray-900">{row.name}</h3>
                              {row.critical ? (
                                <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-900">
                                  Critical
                                </span>
                              ) : null}
                              <span className="text-[10px] font-mono text-gray-400">{row.key}</span>
                            </div>
                            <p className="text-sm text-gray-600 leading-relaxed">{row.trigger}</p>
                            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-500">
                              <div><span className="font-bold text-gray-700">Audience:</span> {row.audience}</div>
                              <div><span className="font-bold text-gray-700">Contains:</span> {row.contains}</div>
                            </div>
                            {row.disable_warning && !on ? (
                              <p className="mt-2 text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                                {row.disable_warning}
                              </p>
                            ) : null}
                          </button>
                          <div className="shrink-0 flex sm:flex-col items-center gap-2">
                            <button
                              type="button"
                              role="switch"
                              aria-checked={on}
                              disabled={!row.can_disable}
                              onClick={() => toggle(row.key, row.can_disable)}
                              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors disabled:opacity-40 ${
                                on ? 'bg-emerald-500' : 'bg-gray-300'
                              }`}
                              title={row.can_disable ? (on ? 'Enabled — click to disable' : 'Disabled — click to enable') : 'Always on'}
                            >
                              <span
                                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                                  on ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                            <span className={`text-[11px] font-bold uppercase tracking-wider ${on ? 'text-emerald-700' : 'text-gray-400'}`}>
                              {on ? 'On' : 'Off'}
                            </span>
                            <button
                              type="button"
                              onClick={() => setPreviewKey(row.key)}
                              className="text-[11px] font-bold text-indigo-700 hover:underline"
                            >
                              Preview
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>

          <aside className="xl:col-span-2 xl:sticky xl:top-6 space-y-4">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-3.5 border-b border-gray-100 bg-slate-900 text-white">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/70">Template preview</div>
                <div className="font-extrabold mt-0.5">{preview?.name || 'Select an email'}</div>
              </div>
              {preview ? (
                <>
                  <div className="px-5 py-3 border-b border-gray-100 text-sm">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Subject</div>
                    <div className="font-bold text-gray-900">{preview.sample_subject || preview.sample_subject}</div>
                  </div>
                  <div className="px-5 py-4 border-b border-gray-100 bg-rose-50/40 space-y-2.5">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Send test email</div>
                    <input
                      type="email"
                      value={testTo}
                      onChange={(e) => setTestTo(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 bg-white"
                    />
                    <button
                      type="button"
                      onClick={sendTest}
                      disabled={testing || !preview?.key}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-bold shadow-sm disabled:opacity-50"
                      style={{ backgroundColor: FLARE_RED }}
                    >
                      <Icon path="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" className="h-4 w-4" />
                      {testing ? 'Sending…' : `Send test · ${preview.name}`}
                    </button>
                    <p className="text-[11px] text-gray-500 leading-relaxed">
                      Sends this template sample to the address above. Appears in Send History with a [TEST] subject.
                    </p>
                  </div>
                  <div className="p-3 bg-gray-100 max-h-[34rem] overflow-auto">
                    <div
                      className="bg-white rounded-xl overflow-hidden shadow-sm"
                      dangerouslySetInnerHTML={{ __html: preview.sample_html || '' }}
                    />
                  </div>
                </>
              ) : (
                <div className="p-8 text-center text-gray-500 text-sm">Choose an email type to preview.</div>
              )}
            </div>
            <div className="rounded-2xl border border-indigo-100 bg-indigo-50/70 p-4 text-sm text-indigo-950">
              <div className="font-extrabold mb-1">How this works</div>
              <ul className="space-y-1.5 text-indigo-900/85 font-medium leading-relaxed">
                <li>• Off emails are logged as <span className="font-black">skipped</span> and never sent via SMTP/SES.</li>
                <li>• OTP / SMTP test types are marked critical — turn off only if you understand signup impact.</li>
                <li>• Order confirmation is sent after website checkout (single + plan).</li>
                <li>• Dispatch emails still require an admin click from Website Orders / Packing Desk.</li>
              </ul>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
